import React, { useState, useEffect, useCallback } from 'react';
import { withFormik, Form, getIn } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import camelCaseRecursive from 'camelcase-keys-recursive';
import { Button, Row, Col, Modal } from 'react-bootstrap';

import { indexEmployeeDncRequest } from '../../requests/dncs';
import FormikSelect from '../../components/Utils/Select/FormikSelect';
import statuses from './formOptions';
import { delayMethod } from '../../services/utils';

const EmployeeCourseForm = props => {
  const { errors, touched, setFieldValue, setFieldTouched, handleClose, employeeCourse, values } = props;
  const [dncTopics, setDncTopics] = useState([]);
  const dispatch = useDispatch();

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 11 }, (_, i) => ({
    value: currentYear - i,
    label: (currentYear - i).toString()
  }));

  const fetchTopics = useCallback(
    (year, currentDncId) => {
      const employeeId = employeeCourse?.employeeId || employeeCourse?.employee_id;

      if (!year || !employeeId) {
        setDncTopics([]);
        return;
      }

      indexEmployeeDncRequest({
        dispatch,
        params: {
          filter_employee: employeeId,
          filter_year: year,
          available_for_courses: currentDncId || true,
          paginate: false
        },
        successCallback: response => {
          const data = camelCaseRecursive(response.data);
          const records = data?.records || data?.data || (Array.isArray(data) ? data : []);

          const options = records.map(item => ({
            value: item.id,
            label: item.topic?.label || 'Sin Tema'
          }));
          setDncTopics(options);
        }
      });
    },
    [dispatch, employeeCourse]
  );

  const { dncYear } = values.employeeCourse;
  const originalDncId = employeeCourse?.employeeDncId;

  useEffect(() => {
    fetchTopics(dncYear, originalDncId);
  }, [dncYear, originalDncId, fetchTopics]);

  return (
    <Form>
      <Modal.Body>
        <Row>
          <Col>
            <FormikSelect
              label="Estado"
              placeholder="Seleccionar Estado"
              options={statuses}
              value={values.employeeCourse.status}
              defaultValue={employeeCourse.status || ''}
              onChange={data => setFieldValue('employeeCourse.status', data ? data.value : '')}
              setFieldTouched={() => setFieldTouched('employeeCourse.status')}
              error={getIn(errors, 'employeeCourse.status')}
              touched={getIn(touched, 'employeeCourse.status')}
            />
          </Col>
        </Row>
        <Row className="mt-3">
          <Col>
            <div className="form-group">
              <label htmlFor="customFile" className="d-block mb-2">
                Certificado :
                <div className="d-flex align-items-center">
                  <div className="custom-file">
                    <input
                      type="file"
                      className="custom-file-input"
                      id="customFile"
                      onChange={e => {
                        setFieldValue('employeeCourse.certificate', e.target.files[0]);
                        setFieldTouched('employeeCourse.certificate');
                      }}
                      accept="application/pdf"
                    />
                    <span className="custom-file-label">
                      {(values.employeeCourse.certificate && values.employeeCourse.certificate.name) ||
                        values.employeeCourse.certificateName ||
                        'Buscar'}
                    </span>
                  </div>
                </div>
              </label>
              <small className="text-muted">Formato sugerido PDF de máximo 5mb.</small>
            </div>
          </Col>
        </Row>
        <Row className="mt-3">
          <Col>
            <FormikSelect
              label="Año DNC"
              placeholder="Seleccionar Año"
              options={years}
              value={values.employeeCourse.dncYear}
              defaultValue={values.employeeCourse.dncYear || ''}
              onChange={data => {
                setFieldValue('employeeCourse.dncYear', data ? data.value : '');
                setFieldValue('employeeCourse.employeeDncId', '');
              }}
              setFieldTouched={() => setFieldTouched('employeeCourse.dncYear')}
              error={getIn(errors, 'employeeCourse.dncYear')}
              touched={getIn(touched, 'employeeCourse.dncYear')}
            />
          </Col>
        </Row>
        <Row className="mt-3">
          <Col>
            <FormikSelect
              label="Capacitaciones Solicitadas"
              placeholder="Seleccionar Tema"
              options={dncTopics}
              value={values.employeeCourse.employeeDncId}
              defaultValue={values.employeeCourse.employeeDncId || ''}
              onChange={data => setFieldValue('employeeCourse.employeeDncId', data ? data.value : '')}
              setFieldTouched={() => setFieldTouched('employeeCourse.employeeDncId')}
              error={getIn(errors, 'employeeCourse.employeeDncId')}
              touched={getIn(touched, 'employeeCourse.employeeDncId')}
              disabled={!values.employeeCourse.dncYear}
            />
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button className="text-center" variant="outline-info" onClick={() => delayMethod(handleClose)}>
          Cancelar
        </Button>
        <Button type="submit" variant="primary">
          Guardar
        </Button>
      </Modal.Footer>
    </Form>
  );
};

const setInitialValues = props => {
  const { employeeCourse } = props;
  return {
    employeeCourse: {
      ...employeeCourse,
      dncYear: employeeCourse.dncYear || '',
      employeeDncId: employeeCourse.employeeDncId || '',
      certificate: null,
      certificateName: employeeCourse.certificateName || ''
    }
  };
};

const validationSchema = Yup.object().shape({
  employeeCourse: Yup.object().shape({
    status: Yup.string().required('Debes escoger un estado')
  })
});

const handleSubmit = (values, { props }) => {
  const { formRequest } = props;
  formRequest(values);
};

export default withFormik({
  mapPropsToValues: setInitialValues,
  validationSchema,
  handleSubmit,
  enableReinitialize: true,
  validateOnMount: false
})(EmployeeCourseForm);
